/**
 * Table Component - Enhanced data table with sorting, pagination, and selection
 */

import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { colors, spacing } from '../../styles/design-tokens';

const Table = React.forwardRef(({
  columns,
  data,
  className = '',
  sortable = true,
  selectable = false,
  pagination = null,
  onRowClick = null,
  emptyState = null,
  loading = false,
  loadingRows = 5,
  variant = 'default',
  ...props
}, ref) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedRows, setSelectedRows] = useState([]);

  // Handle sorting
  const handleSort = (key) => {
    if (!sortable) return;
    if (sortColumn === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(key);
      setSortDirection('asc');
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [data, sortColumn, sortDirection]);

  // Handle row selection
  const toggleRow = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) 
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedRows.length === sortedData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(sortedData.map(row => row.id));
    }
  };

  // Variants
  const variants = {
    default: 'bg-surface border border-neutral-200',
    bordered: 'bg-surface border-2 border-neutral-200',
    stripe: 'bg-surface border border-neutral-200',
    compact: 'bg-surface border border-neutral-200',
  };

  // Loading state
  if (loading) {
    return (
      <div className={`rounded-xl overflow-hidden ${variants[variant] || variants.default} ${className}`}>
        <table className="w-full">
          <thead className="bg-neutral-50">
            <tr>
              {columns.map((col, index) => (
                <th key={index} className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: loadingRows }).map((_, i) => (
              <tr key={i} className="border-t border-neutral-100">
                {columns.map((_, j) => (
                  <td key={j} className="px-6 py-4">
                    <div className="h-4 w-full bg-neutral-100 rounded shimmer" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return emptyState || (
      <div className={`rounded-xl border border-neutral-200 p-12 text-center ${className}`}>
        <p className="text-muted">No data available</p>
      </div>
    );
  }

  const variantStyles = {
    default: 'bg-surface border border-neutral-200',
    bordered: 'bg-surface border-2 border-neutral-200',
    stripe: 'bg-surface border border-neutral-200 [&_tbody_tr:nth-child(odd)]:bg-neutral-50',
    compact: 'bg-surface border border-neutral-200 [&_td]:px-4 [&_td]:py-2 [&_th]:px-4 [&_th]:py-2',
  };

  return (
    <div 
      ref={ref}
      className={`rounded-xl overflow-hidden ${variantStyles[variant] || variantStyles.default} ${className}`}
      {...props}
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-neutral-50 border-b border-neutral-200">
              {selectable && (
                <th className="px-6 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === sortedData.length && sortedData.length > 0}
                    onChange={toggleAll}
                    className="rounded border-neutral-300 text-brand focus:ring-brand"
                  />
                </th>
              )}
              {columns.map((column) => {
                const isSorted = sortColumn === column.key;
                return (
                  <th
                    key={column.key}
                    className={`
                      px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted
                      ${sortable ? 'cursor-pointer hover:text-ink select-none' : ''}
                      ${column.align === 'right' ? 'text-right' : ''}
                      ${column.align === 'center' ? 'text-center' : ''}
                    `}
                    onClick={() => handleSort(column.key)}
                  >
                    <div className={`flex items-center gap-1 ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''}`}>
                      {column.label}
                      {sortable && (
                        <span className="inline-flex">
                          {isSorted ? (
                            sortDirection === 'asc' 
                              ? <ChevronUp size={14} className="text-brand" />
                              : <ChevronDown size={14} className="text-brand" />
                          ) : (
                            <ChevronUp size={14} className="text-neutral-300 opacity-0 group-hover:opacity-100" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {sortedData.map((row, index) => {
              const isSelected = selectedRows.includes(row.id);
              return (
                <tr
                  key={row.id || index}
                  className={`
                    transition-colors hover:bg-neutral-50
                    ${isSelected ? 'bg-brand-soft' : ''}
                    ${onRowClick ? 'cursor-pointer' : ''}
                  `}
                  onClick={() => onRowClick?.(row)}
                >
                  {selectable && (
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-neutral-300 text-brand focus:ring-brand"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`
                        px-6 py-4 text-sm text-ink
                        ${column.align === 'right' ? 'text-right' : ''}
                        ${column.align === 'center' ? 'text-center' : ''}
                        ${column.mono ? 'font-mono' : ''}
                        ${column.truncate ? 'truncate max-w-xs' : ''}
                      `}
                    >
                      {column.render 
                        ? column.render(row[column.key], row)
                        : row[column.key] || '—'
                      }
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <div className="text-sm text-muted">
            Showing {((pagination.page - 1) * pagination.perPage) + 1} to{' '}
            {Math.min(pagination.page * pagination.perPage, pagination.total)} of {pagination.total} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 text-sm border border-neutral-200 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.perPage)}
              className="px-3 py-1 text-sm border border-neutral-200 rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

Table.displayName = 'Table';

export default Table;